/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { jsx } from '@atlaskit/css';
import {
	DefaultExtensionProvider,
	type ExtensionManifest,
} from '@atlaskit/editor-common/extensions';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	ROVO_SKILL_COMBINED_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer';
import { SkillTag } from '@atlassian/rovo-platform-ui-components/skills/SkillTag';

export type SkillQuickInsert = {
	slug: string;
	name: string;
	description: string;
	product?: string;
};

// These three PJM skills require the feature gate to be enabled
const GATED_JIRA_SKILLS = ['create-work-items', 'update-work-items', 'write-status-update'];

// TODO: remove after integration with backend
export const TEMP_SKILLS_LIST: SkillQuickInsert[] = [
	{
		slug: 'update-work-items',
		name: 'Update work items',
		description: 'Update work item fields like assignee, summary, descriptions, and more',
		product: 'Jira',
	},
	{
		slug: 'write-status-update',
		name: 'Write status update',
		description: 'Provide an update on the status of work in a space',
		product: 'Jira',
	},
	{
		slug: 'summarize-release',
		name: 'Summarize release',
		description: 'Summarize release scope, risks, blockers, and more',
		product: 'Jira',
	},
	{
		slug: 'draft-release-notes',
		name: 'Draft release notes',
		description: 'Write release notes covering key changes in a release',
		product: 'Jira',
	},
	{
		slug: 'create-release',
		name: 'Create release',
		description: 'Create a release to package code for deployment',
		product: 'Jira',
	},
	{
		slug: 'explain-next-status',
		name: 'Explain next status',
		description: 'Get a summary of available statuses and transitions for a work item',
		product: 'Jira',
	},
	{
		slug: 'get-data-insights',
		name: 'Get data insights',
		description: 'Create charts and get insights on your work',
		product: 'Jira',
	},
	{
		slug: 'manage-jira-permissions',
		name: 'Manage Jira permissions',
		description: `Manage permissions in Jira projects`,
		product: 'Jira',
	},
	{
		slug: 'find-jira-settings',
		name: 'Find Jira settings',
		description: `Get assistance finding the correct Jira admin settings for your needs`,
		product: 'Jira',
	},
	{
		slug: 'assist-jira-plans',
		name: 'Assist Jira plans',
		description: 'Get help optimizing and managing Jira plans',
		product: 'Jira',
	},
	{
		slug: 'search-assets',
		name: 'Search Assets',
		description: 'Query Assets data to get organizational insights in Rovo Chat',
		product: 'Jira',
	},
];

const icon = () =>
	import(
		/* webpackChunkName: "@atlaskit-internal_editor-skill-icon" */ '@atlaskit/icon-lab/core/skill'
	).then((mod) => mod.default);

const filterSkillsByFeatureGate = (skills: SkillQuickInsert[]): SkillQuickInsert[] => {
	const jiraSkillsEnabled = fg('rovo_jira_skills_enabled');
	if (jiraSkillsEnabled) {
		return skills;
	}

	return skills.filter((skill) => {
		// Include the skill if it's not in the gated list or if the gate is enabled
		return !GATED_JIRA_SKILLS.includes(skill.slug);
	});
};

type UseSkillsManifestProps = {
	skills?: SkillQuickInsert[];
	isConversationAgentic: boolean;
	promptHasSkill: boolean;
};

export const useSkillsManifest = ({
	skills = TEMP_SKILLS_LIST,
	isConversationAgentic,
	promptHasSkill,
}: UseSkillsManifestProps): DefaultExtensionProvider<SkillQuickInsert>[] | undefined => {
	const createManifest = React.useCallback((skills: SkillQuickInsert[] = TEMP_SKILLS_LIST) => {
		const filteredSkills = filterSkillsByFeatureGate(skills);

		const manifest: ExtensionManifest<SkillQuickInsert> = {
			title: 'Skills',
			type: ROVO_SKILL_EXTENSION_TYPE,
			key: ROVO_SKILL_EXTENSION_KEY,
			categories: ['Skills'],
			icons: {
				'16': icon,
				'24': icon,
				'48': icon,
			},
			modules: {
				quickInsert: filteredSkills.map((skill) => {
					const { slug, description, name, product } = skill;
					return {
						key: slug,
						keywords: ['skills', 'skill', slug, product ?? ''],
						// 1 is the default priority
						priority: 99,
						title: name,
						description: description,
						action: async () => {
							return [
								{
									type: 'inlineExtension',
									attrs: {
										extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
										extensionType: ROVO_SKILL_EXTENSION_TYPE,
										parameters: skill,
									},
								},
								// render an empty space after the skill tag
								{ type: 'text', text: ' ' },
							];
						},
					};
				}),
				nodes: {
					skillTag: {
						type: 'inlineExtension',
						render:
							async () =>
							({ node }) => {
								const params = node.parameters;
								if (params) {
									return <SkillTag {...params} />;
								}
								return null;
							},
					},
				},
			},
		};

		return manifest;
	}, []);

	const manifestProvider = React.useMemo(() => {
		// skills are only applicable to non-agentic conversations
		// remove skills if a skill is already present in the prompt until chaining is allowed
		if ((isConversationAgentic || promptHasSkill) && fg('rovo_chat_enable_skills_ui_m1')) {
			return [new DefaultExtensionProvider([createManifest([])])];
		} else if (fg('rovo_chat_enable_skills_ui_m1')) {
			return [new DefaultExtensionProvider([createManifest(skills)])];
		}
		return undefined;
	}, [isConversationAgentic, promptHasSkill, createManifest, skills]);

	return manifestProvider;
};
